import styled, { css, DefaultTheme, keyframes } from 'styled-components'

import { ToastType } from './toast.types'

const slideIn = keyframes`
  from {
    transform: translateX(-100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`

// Note: keeps the toast's own spacing until it has slid out, then collapses it so the rest slide down
const slideOut = (theme: DefaultTheme) => keyframes`
  0% {
    transform: translateX(0);
    opacity: 1;
    max-height: 30rem;
    margin-bottom: ${theme.space['8']};
    padding-top: ${theme.space['8']};
    padding-bottom: ${theme.space['8']};
  }
  50% {
    transform: translateX(-100%);
    opacity: 0;
    max-height: 30rem;
    margin-bottom: ${theme.space['8']};
    padding-top: ${theme.space['8']};
    padding-bottom: ${theme.space['8']};
  }
  100% {
    transform: translateX(-100%);
    opacity: 0;
    max-height: 0;
    margin-bottom: ${theme.space['0']};
    padding-top: ${theme.space['0']};
    padding-bottom: ${theme.space['0']};
  }
`

export const CloseButton = styled.button(
  ({ theme }) => css`
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    padding: ${theme.space['6']};
    background: none;
    border: none;
    border-radius: ${theme.border.radius['4']};
    color: inherit;
    cursor: pointer;
    transition: background-color ${theme.transition.duration.sm};
  `,
)

export const Toast = styled.div<{ $type: ToastType; $isExiting?: boolean }>(
  ({ theme, $type, $isExiting }) => {
    const styles = {
      error: css`
        background-color: ${theme.colors.red200};
        color: ${theme.colors.red500};

        ${CloseButton}:hover {
          background-color: ${theme.colors.red300};
        }
      `,
      success: css`
        background-color: ${theme.colors.green200};
        color: ${theme.colors.green500};

        ${CloseButton}:hover {
          background-color: ${theme.colors.green300};
        }
      `,
    }

    // Note: the design system toast (Figma "Toasts"): 8px / 16px padding, 24px before the
    // close button, and the width hugs the text up to a cap so long messages wrap
    return css`
      display: flex;
      align-items: center;
      gap: ${theme.space['24']};
      width: fit-content;
      max-width: 40rem;
      padding: ${theme.space['8']} ${theme.space['16']};
      margin-bottom: ${theme.space['8']};
      border-radius: ${theme.border.radius['8']};
      overflow: hidden;
      animation: ${$isExiting ? slideOut(theme) : slideIn}
        ${$isExiting ? theme.transition.duration.lg : theme.transition.duration.md} ease-out
        forwards;

      ${styles[$type]}
    `
  },
)

// Note: the icon sits on the first text line, so it stays put when the text wraps or has a description
export const Content = styled.div(
  ({ theme }) => css`
    display: flex;
    align-items: flex-start;
    gap: ${theme.space['8']};
    min-width: 0;
  `,
)

export const Text = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  font-size: 1.4rem;
  line-height: 2rem;
  word-break: break-word;
`

export const Message = styled.p`
  font-weight: 600;
`

export const Description = styled.p`
  font-weight: 400;
`
